import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@hqo/ui-atoms';
import { cn } from '@hqo/react-hooks';
import {
  format,
  getDaysInMonth,
  startOfMonth,
  endOfMonth,
  parseISO,
  isSameDay,
  isBefore,
  isAfter,
  isToday,
  isValid,
  addMonths,
  subMonths,
  eachDayOfInterval,
  startOfWeek,
  endOfWeek,
  isSameMonth,
  type Locale,
  type Day,
} from 'date-fns';

type CalendarMode = 'single' | 'range' | 'multiple';

type SingleSelected = string;
type RangeSelected = { from: string; to: string | null } | null;
type MultipleSelected = string[];

type CalendarSelected = SingleSelected | RangeSelected | MultipleSelected;

interface CalendarProps {
  selected?: CalendarSelected;
  onSelect?: (selected: CalendarSelected) => void;
  className?: string;
  mode?: CalendarMode;
  disablePast?: boolean;
  disableAfter?: Date;
  locale: Locale;
  shouldDisableDate?: (date: Date) => boolean;
  todayLabel?: string;
  selectedLabel?: string;
  maxSelected?: number;
}

export const Calendar = ({
  selected,
  onSelect,
  className,
  mode = 'single',
  disablePast = true,
  disableAfter = addMonths(new Date(), 6),
  locale,
  shouldDisableDate,
  todayLabel = 'Today',
  selectedLabel = 'Selected',
  maxSelected,
}: CalendarProps): JSX.Element => {
  const [currentMonth, setCurrentMonth] = React.useState<Date>(() => {
    try {
      if (!selected) {
        return new Date();
      }
      if (
        mode === 'range' &&
        selected &&
        typeof selected === 'object' &&
        !Array.isArray(selected) &&
        'from' in selected
      ) {
        const rangeSelected = selected as RangeSelected;
        if (rangeSelected?.from) {
          return parseISO(rangeSelected.from);
        }
      }
      if (Array.isArray(selected) && selected[0]) {
        return parseISO(selected[0]);
      }
      if (typeof selected === 'string') {
        return parseISO(selected);
      }
      return new Date();
    } catch (e) {
      return new Date();
    }
  });

  const [rangeStart, setRangeStart] = React.useState<string | null>(null);
  const [rangeEnd, setRangeEnd] = React.useState<string | null>(null);
  // Track if last click set a single day (to determine if next click should create range)
  // Using ref so it persists when calendar closes/reopens
  const lastActionWasSingleDayRef = React.useRef<boolean>(false);

  // Initialize and sync range state
  React.useEffect(() => {
    if (
      mode === 'range' &&
      selected &&
      typeof selected === 'object' &&
      !Array.isArray(selected) &&
      'from' in selected
    ) {
      const rangeSelected = selected as RangeSelected;
      const newStart = rangeSelected?.from || null;
      const newEnd = rangeSelected?.to || null;

      // Only update if values actually changed
      if (newStart !== rangeStart || newEnd !== rangeEnd) {
        setRangeStart(newStart);
        setRangeEnd(newEnd);

        // If external change resulted in a range (not single day), reset the flag
        if (newStart && newEnd && newStart !== newEnd) {
          lastActionWasSingleDayRef.current = false;
        }
      }
    }
  }, [mode, selected, rangeStart, rangeEnd]);

  // Sync currentMonth with selected date when it changes
  React.useEffect(() => {
    try {
      let selectedDate: Date | null = null;

      // Parse selected date based on mode
      if (mode === 'single' && typeof selected === 'string' && selected) {
        selectedDate = parseISO(selected);
      } else if (
        mode === 'range' &&
        selected &&
        typeof selected === 'object' &&
        !Array.isArray(selected) &&
        'from' in selected
      ) {
        const rangeSelected = selected as RangeSelected;
        if (rangeSelected?.from) {
          selectedDate = parseISO(rangeSelected.from);
        }
      } else if (mode === 'multiple' && Array.isArray(selected) && selected.length > 0 && selected[0]) {
        selectedDate = parseISO(selected[0]);
      }

      // Update currentMonth only if selected date is valid and in a different month
      if (selectedDate && isValid(selectedDate)) {
        setCurrentMonth((prevMonth) => {
          // Only update if the selected date is in a different month
          return isSameMonth(selectedDate, prevMonth) ? prevMonth : selectedDate;
        });
      }
    } catch (e) {
      // Silently ignore invalid dates
    }
  }, [selected, mode]);

  // Get first day of week based on locale (0 = Sunday, 1 = Monday, etc.)
  const getFirstDayOfWeek = (): Day => {
    // date-fns v4 locales have options.weekStartsOn
    if (locale?.options?.weekStartsOn !== undefined) {
      return locale.options.weekStartsOn;
    }
    // Default to Sunday (0) if not specified
    return 0 as Day;
  };

  // Get month names in locale
  const getMonthName = (date: Date): string => {
    return format(date, 'MMMM', { locale });
  };

  // Get day names in locale
  const getDayNames = (): string[] => {
    const firstDay = getFirstDayOfWeek();
    const weekStart = startOfWeek(new Date(), { weekStartsOn: firstDay, locale });
    const weekDays = eachDayOfInterval({
      start: weekStart,
      end: endOfWeek(weekStart, { weekStartsOn: firstDay, locale }),
    });

    return weekDays.map((day) => format(day, 'EEE', { locale }));
  };

  const previousMonth = React.useCallback((): void => {
    setCurrentMonth((prev) => subMonths(prev, 1));
  }, []);

  const nextMonth = React.useCallback((): void => {
    setCurrentMonth((prev) => addMonths(prev, 1));
  }, []);

  const isPreviousMonthDisabled = React.useMemo(() => {
    if (!disablePast) {
      return false;
    }
    const today = new Date();
    return isSameMonth(currentMonth, today) || isBefore(currentMonth, today);
  }, [disablePast, currentMonth]);

  const isNextMonthDisabled = React.useMemo(() => {
    if (!disableAfter) {
      return false;
    }
    return !isBefore(endOfMonth(currentMonth), disableAfter);
  }, [disableAfter, currentMonth]);

  const handleDateClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      if (!onSelect) {
        return;
      }

      const dateString = event.currentTarget.getAttribute('data-date');
      if (!dateString) {
        return;
      }

      const date = parseISO(dateString);

      if (mode === 'range') {
        // Check if we have a single day selected (start and end are the same)
        const isSingleDay = rangeStart && rangeEnd && rangeStart === rangeEnd;

        if (!rangeStart) {
          // No selection: Set both start and end to clicked date (single day)
          setRangeStart(dateString);
          setRangeEnd(dateString);
          lastActionWasSingleDayRef.current = true;
          onSelect({ from: dateString, to: dateString });
        } else if (isSingleDay && lastActionWasSingleDayRef.current) {
          // Single day selected AND last action was setting that single day: Create a range
          const start = parseISO(rangeStart);
          const end = date;

          if (isSameDay(start, end)) {
            // Clicking the same date - keep as single day
            return;
          }

          if (isAfter(end, start)) {
            // Normal range: start to end
            setRangeEnd(dateString);
            lastActionWasSingleDayRef.current = false;
            onSelect({ from: rangeStart, to: dateString });
          } else {
            // Swap: end to start
            setRangeStart(dateString);
            setRangeEnd(rangeStart);
            lastActionWasSingleDayRef.current = false;
            onSelect({ from: dateString, to: rangeStart });
          }
        } else {
          // All other cases: Replace with new single day
          // This includes: range selected, or single day from preset
          setRangeStart(dateString);
          setRangeEnd(dateString);
          lastActionWasSingleDayRef.current = true;
          onSelect({ from: dateString, to: dateString });
        }
      } else if (mode === 'multiple') {
        const current = Array.isArray(selected) ? selected : [];
        let next: string[];
        if (current.includes(dateString)) {
          next = current.filter((value) => value !== dateString);
        } else if (maxSelected != null && current.length >= maxSelected) {
          next = current;
        } else {
          next = [...current, dateString];
        }
        onSelect(next);
      } else {
        onSelect(dateString);
      }
    },
    [onSelect, mode, rangeStart, rangeEnd, selected, maxSelected],
  );

  const isInRange = (date: Date): boolean => {
    if (mode !== 'range' || !rangeStart) {
      return false;
    }

    const dateString = format(date, 'yyyy-MM-dd');

    if (!rangeEnd) {
      return dateString === rangeStart;
    }

    const start = parseISO(rangeStart);
    const end = parseISO(rangeEnd);

    return (isAfter(date, start) || isSameDay(date, start)) && (isBefore(date, end) || isSameDay(date, end));
  };

  const isRangeEdge = (date: Date): boolean => {
    if (mode !== 'range' || !rangeStart) {
      return false;
    }

    const dateString = format(date, 'yyyy-MM-dd');

    return dateString === rangeStart || dateString === rangeEnd;
  };

  const isSelected = (date: Date): boolean => {
    if (!selected) {
      return false;
    }

    if (mode === 'range') {
      return isInRange(date);
    }

    if (Array.isArray(selected)) {
      // Multi-select mode
      const dateString = format(date, 'yyyy-MM-dd');
      return selected.includes(dateString);
    }
    // Single select mode
    try {
      const selectedDate = parseISO(selected as string);
      return isSameDay(date, selectedDate);
    } catch (e) {
      return false;
    }
  };

  const isDateToday = (date: Date): boolean => {
    return isToday(date);
  };

  const isDatePast = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return isBefore(checkDate, today);
  };

  const renderDays = (): JSX.Element[] => {
    const days: JSX.Element[] = [];
    const monthStart = startOfMonth(currentMonth);
    const firstDayOfWeek = getFirstDayOfWeek();

    // Get the start of the calendar grid (first day of week that contains month start)
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: firstDayOfWeek, locale });

    // Get the end of the calendar grid (last day of week that contains month end)
    const monthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), getDaysInMonth(currentMonth));
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: firstDayOfWeek, locale });

    // Get all days in the calendar view (6 weeks = 42 days)
    const allDays = eachDayOfInterval({
      start: calendarStart,
      end: calendarEnd,
    });

    // Render all days
    allDays.forEach((date) => {
      const isCurrentMonth = isSameMonth(date, currentMonth);
      const isDateSelected = isSelected(date);
      const today = isDateToday(date);
      const past = isDatePast(date);
      const inRange = mode === 'range' && isInRange(date);
      const rangeEdge = mode === 'range' && isRangeEdge(date);

      // Check if date should be disabled
      const customDisabled = shouldDisableDate ? shouldDisableDate(date) : false;
      const isDisabled =
        customDisabled || (disablePast && past) || !isCurrentMonth || (disableAfter && isAfter(date, disableAfter));

      const dateKey = date.toISOString();
      const dateString = format(date, 'yyyy-MM-dd');

      days.push(
        <div key={dateKey} className="flex items-center justify-center">
          <button
            type="button"
            data-date={dateString}
            onClick={handleDateClick}
            disabled={isDisabled}
            aria-disabled={isDisabled}
            className={cn(
              'w-9 h-9 sm:w-10 sm:h-10 rounded-lg text-sm font-medium transition-all hover:bg-gray-100 flex items-center justify-center touch-manipulation',
              rangeEdge && 'bg-primary text-white hover:bg-primary/90',
              inRange && !rangeEdge && 'bg-primary/20 text-primary hover:bg-primary/30',
              isDateSelected && mode !== 'range' && 'bg-primary text-white hover:bg-primary/90',
              today && !isDateSelected && !inRange && 'border-2 border-primary text-primary',
              isDisabled && 'text-gray-300 cursor-not-allowed opacity-50 hover:bg-transparent hover:text-gray-300',
              !isCurrentMonth && 'opacity-40',
              !isDateSelected && !today && !isDisabled && !inRange && 'text-gray-700',
            )}
          >
            {format(date, 'd', { locale })}
          </button>
        </div>,
      );
    });

    return days;
  };

  return (
    <div className={cn('w-full max-w-md mx-auto', className)}>
      {/* Month/Year Header */}
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={previousMonth}
          disabled={isPreviousMonthDisabled}
          className="h-8 w-8 sm:h-9 sm:w-9"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <h3 className="text-base sm:text-lg font-semibold text-gray-900">
          {getMonthName(currentMonth)} {currentMonth.getFullYear()}
        </h3>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={nextMonth}
          disabled={isNextMonthDisabled}
          className="h-8 w-8 sm:h-9 sm:w-9"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Day Names */}
      <div className="grid grid-cols-7 mb-2">
        {getDayNames().map((day) => (
          <div
            key={day}
            className="flex items-center justify-center text-xs font-semibold text-gray-500 uppercase h-7 sm:h-8"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Days */}
      <div className="grid grid-cols-7 gap-1 sm:gap-2">{renderDays()}</div>

      {/* Legend */}
      <div className="mt-4 flex items-center justify-center gap-4 text-xs text-gray-600">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded border-2 border-primary" />
          <span>{todayLabel}</span>
        </div>
        {selected && (
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-primary" />
            <span>{selectedLabel}</span>
          </div>
        )}
      </div>
    </div>
  );
};
